import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, Observable } from 'rxjs';
import { EventLogInvalidUserCreateRequest, FunctionResult, UserInfoViewModel } from '../models/viewmodels';
import { environment } from 'src/environments/environment';
import { AppUser } from '../models/dbmodels';
import { AuthService } from './auth.service';
import { DataService } from './data-api.service';
import { ActivatedRoute, ActivatedRouteSnapshot, Data, NavigationEnd, Router } from '@angular/router';
import { CompanyRoleEnum, UserRoleEnumNumeric } from '../models/lookup-view-models';
import { EventLogService } from './event-log.service';

@Injectable({
  providedIn: 'root'
})
export class UserInfoService {

  private userInfoSubject = new BehaviorSubject<UserInfoViewModel | null>(null);

  constructor(private dataService: DataService, private authService: AuthService, private router: Router, private eventLogService: EventLogService) {
    let routeDataInitialized = false;

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const routeSnapshot = this.router.routerState.snapshot.root;
        const activeRoute = this.findDeepestChild(routeSnapshot);
        if(activeRoute.data && !routeDataInitialized) {
          routeDataInitialized = true;
          const anonymousRoute = activeRoute.data?.['anonymous'] === true;
          if(!anonymousRoute){
            this.refreshUserInfo();

          }
        }
      });
    ;
  }

  private findDeepestChild(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

async refreshUserInfo(): Promise<void> {
  // 1) Get the Observable from your dataService
  // passes client id
  const lineItemObs = this.dataService.getCurrentUserInfo();

  // 2) Convert that Observable into a Promise that waits for its first (and only) emission
  const data = await firstValueFrom(lineItemObs) as any;

  // 3) Once we get 'data', map and cache it
  if (data !== null) {

    var currentUserId = this.getAppUser()?.UserId; //comes from local storage
    var newUserId = data?.AppUser?.UserId;

    //this does happen when a user clicks on an email
    if (currentUserId && newUserId && currentUserId !== newUserId) {
      console.log('UserInfoService.refreshUserInfo: UserId changed from', currentUserId, 'to', newUserId, 'RETRYING REQUEST');

      //redo the request and await should hold up here
      let lineItemObs2 = this.dataService.getCurrentUserInfo();
      let data2 = await firstValueFrom(lineItemObs2) as any;
      let newUserId2 = data2?.AppUser?.UserId;

      if (newUserId2 && currentUserId !== newUserId2) {
        console.log('UserInfoService.refreshUserInfo: UserId after 2nd attempt', newUserId2, currentUserId);
        try { 
          var evt: EventLogInvalidUserCreateRequest = {
            ExpectedValue: Number(currentUserId),
            ActualValue: Number(newUserId),
            RequestUrl: `UserInfoService.refreshUserInfo currentUserId ${currentUserId}, newUserId1 ${newUserId}, newUserId2 ${newUserId2} - api/appuser/getCurrentUserInfo`,
            LocationUrl: window.location.href
          };
          this.eventLogService.createInvalidUserLogoutEvent(evt);
        } catch (e) {
          console.error('error when logging invalid user', e, currentUserId, newUserId);
        }

        let savedUserInfo = this.getUserInfo();
        if (savedUserInfo && savedUserInfo.AppUser) {
          this.userInfoSubject.next(savedUserInfo);
        } 

        //this.authService.logout();
        return; //bail out
      } else {
        //proceed with new data
        console.log('UserInfoService.refreshUserInfo: UserId after 2nd attempt is now', newUserId2, 'proceeding');
      }
    }

    const userInfo: UserInfoViewModel  = {
      ClientId: data.ClientId,
      AppUser: data.AppUser,
      Employees: data.Employees,
      UserPermissions:  data.UserPermissions.Permissions,
      EmployeePermissions: data.EmployeePermissions
    };

    this.setUserInfo(userInfo);
    this.userInfoSubject.next(userInfo);

  } else {
    console.warn('UserInfoService.refreshUserInfo: received null from API');
  }

}

  getAppUser(): AppUser {
    let user = this.getUserInfo()?.AppUser ?? {} as AppUser;
    return user;
  }

  hasCustomerRole(roleEnum : CompanyRoleEnum): boolean {
    let userInfo = this.getUserInfo();
    var companyRoles = userInfo?.Employees?.flatMap(e => e?.CompanyRoles);
    var _hadCustomerRole = companyRoles?.some(role => role.CompanyRoleLookup["Value"] === roleEnum);
    return _hadCustomerRole;
  }
  hasUserRole(roleEnum: UserRoleEnumNumeric): boolean {
    let userInfo = this.getUserInfo();
    let userRoles = userInfo?.AppUser?.UserRoles;
    var _hasUserRole = userRoles?.some(role => role.UserRoleLookup["Value"] === roleEnum);

    return _hasUserRole;
  }
  IsImpersonating() {
    let impersonating: boolean = false;
    let token = this.authService.getDecodedAccessToken();
    //console.log('UserInfoService.getImpersonatorUserId', token);
    if (token?.ImpersonatorUserId !== undefined) {
      impersonating = token.ImpersonatorUserId > 0;
    } else {
      impersonating = false;
    }
    return impersonating;
  }

  async startImpersonation(userId: number): Promise<FunctionResult> {
    let result: FunctionResult = {
      Successful: false,
      Message: ''
    };

    let userInfo = this.getUserInfo();
    if (userInfo?.AppUser?.IsAppAdmin || userInfo?.AppUser?.IsSysAdmin) {
      if (this.IsImpersonating() == false) {
        localStorage.setItem('ImpersonatingUserId', userId.toString());
        this.dataService.putImpersonateUserStart(userId).subscribe((data: any) => {
          console.log('user service start impersonation', 'impersonation response', data);
          result.Successful = true;
          this.setUserInfo(data);
          this.refreshUserInfo().then(() => {
            //console.log('user service start impersonation', 'refreshUserInfo', data);
            if (data.AppUser.IsAppAdmin) {
              window.location.reload();
            } else {
              window.location.href = '/topmenu/project/';
            }
          });
        });
      } else {
        result.Successful = false;
        result.Message = 'You are already impersonating another user.  Exit or log out of current impersonation to start a new one.';
        //console.log('user service start impersonation', 'already impersonating', this.getUserInfo());
      }
    } else {
      result.Successful = false;
      result.Message = 'You are not authorized to impersonate another user.';
      console.log('user service start impersonation', 'not authorized', this.getUserInfo());
    }

    return result;
  }

  stopImpersonation() {
    localStorage.setItem('ImpersonatingUserId', '???');  
    let requestObservable = this.dataService.putImpersonateUserStop();
    requestObservable.subscribe((data: UserInfoViewModel) => {
      console.log('user service stop impersonation', 'impersonation response', data);
      this.setUserInfo(data);
      setTimeout(() => {
        localStorage.removeItem('ImpersonatingUserId');
        if (data.AppUser.IsAppAdmin) {
          window.location.reload();
        } else {
          window.location.href = '/topmenu/project/';
        }
      }, 1);
    });
  }

  getImpersonatorUserId(): number {
    let token = this.authService.getDecodedAccessToken();
    //console.log('UserInfoService.getImpersonatorUserId', token);
    if (token?.ImpersonatorUserId !== undefined) {
      return token.ImpersonatorUserId;
    } else {
      return -1;
    }
  }

  async getLogoImageUrlAsync(): Promise<string> {

    let logoPath = '';

    try {
      let userInfo = this.getUserInfo();
      let defaultCompany = userInfo?.AppUser?.DefaultCompany;
      if (!defaultCompany) {
        let result = this.dataService.getBrandLogoImage(2).then((data: any) => {
          logoPath = URL.createObjectURL(data);
          return logoPath;
        });
        return result;

      } else if (defaultCompany?.LogoFileName != null && defaultCompany?.LogoFileName.length > 2) {
        let result = (this.dataService.getCompanyLogoImage(defaultCompany.CompanyId).then((data: any) => {
          console.log('getCompanyLogoImage - response', data);
          logoPath = URL.createObjectURL(data);
          return logoPath
        }));
        return result;
      } else {
        //use company group
        let brand = defaultCompany?.DefaultBrand;
        let result = this.dataService.getBrandLogoImage(brand.BrandId).then((data: any) => {
          logoPath = URL.createObjectURL(data);
          return logoPath;
        });
        return result;
      }

    } catch (error) {
      console.log('UserInfoService.getLogoImage error', error);
      logoPath = 'assets/company/default/ae-air-logo-main.png';

    }

    return null;
  }


  getLogoImageUrl(): string {

    let logoPath = '';

    try {
      let userInfo = this.getUserInfo();
      let defaultCompany = userInfo?.AppUser?.DefaultCompany;
      console.log('UserInfoService.getLogoImage', 'defaultCompany', defaultCompany);

      if (!defaultCompany) {
        logoPath = environment.defaultLogoImagePath;
      } else if (defaultCompany?.LogoFileName != null && defaultCompany?.LogoFileName.length > 2) {
        let result = this.dataService.getCompanyLogoImage(defaultCompany.CompanyId).then((data: any) => {
          console.log('getCompanyLogoImage - response', data);
          logoPath = URL.createObjectURL(data);
        });

      } else {
        //use company group
        let brand = defaultCompany?.DefaultBrand;
        let result = this.dataService.getBrandLogoImage(brand.BrandId).then((data: any) => {
          console.log('getCompanyLogoImage - response', data);
          logoPath = URL.createObjectURL(data);
        });
      }

    } catch (error) {
      console.log('UserInfoService.getLogoImage error', error);
      logoPath = 'assets/company/default/ae-air-logo-main.png';

    }

    return logoPath;
  }

  getBrandName() {
    // brand id comes from the default company company group
    let userInfo: UserInfoViewModel = this.getUserInfo();
    let brandName = userInfo?.AppUser?.DefaultCompany?.DefaultBrand.Name;
    if (!brandName) {
      console.log('UserInfoService.getBrandId', 'brandId not found');
      brandName = "environment.defaultBrandName";
    }
    return brandName;
  }

  getBrandClass(): string {
    // brand id comes from the default company company group
    let userInfo: UserInfoViewModel = this.getUserInfo();
    let brandClass = userInfo?.AppUser?.DefaultCompany?.DefaultBrand?.CssClass;
    if (!brandClass) {
      brandClass = environment.defaultBrandClass;
    }
    return brandClass;
  }

  setUserInfo(userInfo: UserInfoViewModel) {
    localStorage.setItem('userInfo', JSON.stringify(userInfo));
  }

  getUserInfo(): UserInfoViewModel {
    let rtn: UserInfoViewModel = {}; // as UserInfoViewModel;
    try {
      let jsonStr = localStorage.getItem('userInfo');
      let userInfo: UserInfoViewModel = JSON.parse(jsonStr);

      rtn = userInfo;

    } catch (error) {
      console.log('UserInfoService.getUserInfo error', error);
    }
    return rtn;
  }

  getUserInfo$(): Observable<UserInfoViewModel | null> {
    return this.userInfoSubject.asObservable();
  }

  LogOut() {
    this.authService.logout();
  }

  //special function to easily get if user is an engineer
  //to match api, if a user is an engineering for any company, then they will not be able to see quotes and pricing data
  //for any company since an engineer can be added to any company's projects
  isEngineer(): boolean {
    let isEngineer = false;
    try {
        let userEmployees = this.getUserInfo()?.Employees ?? [];

        if (userEmployees.length === 0) {
            return false;
        }

        let userRoles = userEmployees.map(emp => emp.CompanyRoles).flat();
        isEngineer = userRoles.some(role => role.RoleName === 'Engineer');
        //console.log('UserInfoService.isEngineer', 'userEmployees', userEmployees, 'userRoles', userRoles, 'isEngineer', isEngineer);
    } catch (error) {
        console.error('UserInfoService.isEngineer error', error);
        isEngineer = false;
    }

    return isEngineer;
  }

}
